/* =============================================================
   HEDAYAT MUSIC — Lecteur Avant/Après "Halo" — module multi-instances
   - Plusieurs lecteurs par page, chacun indépendant (ses propres fichiers).
   - Un seul son à la fois sur la page (bus global : démarrer coupe les autres).
   - Auto-montage : chaque element [data-hm-avantapres] contenant un
     <script type="application/json"> de config est monté automatiquement.
   ============================================================= */
(function () {
  'use strict';
  if (window.HMAvantApres) { window.HMAvantApres.boot(); return; }

  // ---------- AudioContext partagé (un seul pour toute la page) ----------
  var _ctx = null;
  function getCtx() { if (!_ctx) { _ctx = new (window.AudioContext || window.webkitAudioContext)(); } return _ctx; }

  // ---------- iOS : jouer meme en mode silencieux (bascule le canal audio sur "media") ----------
  var _isIOS = /iP(hone|od|ad)/.test(navigator.platform || '') || ((navigator.platform === 'MacIntel') && navigator.maxTouchPoints > 1) || /iPhone|iPad|iPod/.test(navigator.userAgent || '');
  var _iosUnlock = (function () {
    if (!_isIOS) return function () {};
    var tag = null;
    function silentWav() {
      var sr = 8000, n = sr / 2, buf = new ArrayBuffer(44 + n), dv = new DataView(buf);
      function ws(o, str) { for (var i = 0; i < str.length; i++) dv.setUint8(o + i, str.charCodeAt(i)); }
      ws(0, 'RIFF'); dv.setUint32(4, 36 + n, true); ws(8, 'WAVE'); ws(12, 'fmt ');
      dv.setUint32(16, 16, true); dv.setUint16(20, 1, true); dv.setUint16(22, 1, true);
      dv.setUint32(24, sr, true); dv.setUint32(28, sr, true); dv.setUint16(32, 1, true); dv.setUint16(34, 8, true);
      ws(36, 'data'); dv.setUint32(40, n, true);
      var u = new Uint8Array(buf); for (var i = 0; i < n; i++) u[44 + i] = 128;
      var bin = ''; for (var j = 0; j < u.length; j++) bin += String.fromCharCode(u[j]);
      return 'data:audio/wav;base64,' + btoa(bin);
    }
    return function () {
      try {
        if (!tag) {
          tag = document.createElement('audio');
          tag.setAttribute('playsinline', ''); tag.setAttribute('webkit-playsinline', '');
          tag.setAttribute('x-webkit-airplay', 'deny');
          tag.loop = true; tag.preload = 'auto';
          try { tag.disableRemotePlayback = true; } catch (e) {}
          tag.src = silentWav(); tag.load();
        }
        var pr = tag.play(); if (pr && pr.catch) pr.catch(function () {});
      } catch (e) {}
    };
  })();

  // ---------- BUS global : un seul lecteur joue à la fois ----------
  var _active = null;
  var BUS = {
    claim: function (e) { if (_active && _active !== e) { try { _active.pause(); } catch (err) {} } _active = e; },
    release: function (e) { if (_active === e) _active = null; }
  };


  // ---------- théorie ----------
  const mtof = (m) => 440 * Math.pow(2, (m - 69) / 12);
  // progressions (degrés MIDI, octave centrale) + paramètres par genre
  const GENRES = {
    drill:  { bpm: 142, swing: 0.0, root: 40, prog: [0, 0, -2, 3], scale: [0,2,3,5,7,8,10],
              kick: [0,6,10], snare: [4,12], hat: 'trap', padGain: 0.10, leadGain: 0.16, mood: 'dark' },
    pop:    { bpm: 104, swing: 0.04, root: 48, prog: [0, 5, -3, -1], scale: [0,2,4,5,7,9,11],
              kick: [0,8], snare: [4,12], hat: 'eighth', padGain: 0.16, leadGain: 0.13, mood: 'bright' },
    afro:   { bpm: 110, swing: 0.08, root: 45, prog: [0, 3, 5, 3], scale: [0,2,4,5,7,9,11],
              kick: [0,6,10], snare: [4,12], hat: 'afro', padGain: 0.13, leadGain: 0.15, mood: 'warm' },
    rnb:    { bpm: 88, swing: 0.06, root: 44, prog: [0, -4, 2, -2], scale: [0,2,3,5,7,9,10],
              kick: [0,7], snare: [4,12], hat: 'eighth', padGain: 0.18, leadGain: 0.11, mood: 'warm' },
  };

  // génère la partition (mêmes notes pour avant & après)
  function buildScore(g, variant) {
    variant = variant || 0;
    const base = GENRES[g];
    const cfg = Object.assign({}, base);
    if (variant) {
      const TR = [0, 5, -4, 3];
      cfg.root = base.root + TR[variant % TR.length];
      const rr = variant % base.prog.length;
      cfg.prog = base.prog.slice(rr).concat(base.prog.slice(0, rr));
    }
    const spb = 60 / cfg.bpm;          // secondes par temps
    const bars = 4, beatsPerBar = 4;
    const stepsPerBeat = 4;
    const stepDur = spb / stepsPerBeat;
    const duration = bars * beatsPerBar * spb;
    const ev = { duration, cfg, kick: [], snare: [], hat: [], bass: [], pad: [], lead: [] };
    const chordRoot = (bar) => cfg.root + cfg.prog[bar % cfg.prog.length];
    // pad : un accord tenu par mesure (triade sur la gamme)
    for (let b = 0; b < bars; b++) {
      const r = chordRoot(b);
      const t = b * beatsPerBar * spb;
      ev.pad.push({ t, dur: beatsPerBar * spb, notes: [r, r + 3 + (cfg.mood==='bright'?1:0), r + 7, r + 12] });
      ev.bass.push({ t, dur: spb * 1.5, note: r - 12 });
      ev.bass.push({ t: t + spb * 2, dur: spb * 1.2, note: r - 12 });
    }
    // drums + lead par step
    let li = 0;
    const sc = cfg.scale;
    for (let b = 0; b < bars; b++) {
      for (let beat = 0; beat < beatsPerBar; beat++) {
        const stepInBar = beat * stepsPerBeat;
        const tBeat = (b * beatsPerBar + beat) * spb;
        // kick / snare placés sur la grille 16
        cfg.kick.forEach((k) => { if (k % stepsPerBeat === 0 ? (k===stepInBar) : false) {} });
      }
    }
    // simpler 16-step grid for the whole bar pattern, repeated each bar
    for (let b = 0; b < bars; b++) {
      const barT = b * beatsPerBar * spb;
      const r = chordRoot(b);
      for (let s = 0; s < 16; s++) {
        const sw = (s % 2 === 1) ? cfg.swing * stepDur : 0;
        const t = barT + s * stepDur + sw;
        if (cfg.kick.includes(s)) ev.kick.push({ t });
        if (cfg.snare.includes(s)) ev.snare.push({ t });
        // hats
        let hit = false;
        if (cfg.hat === 'eighth') hit = (s % 2 === 0);
        else if (cfg.hat === 'trap') hit = (s % 2 === 0) || (s % 8 === 6) || (s % 8 === 7);
        else if (cfg.hat === 'afro') hit = (s % 2 === 0) || (s === 7) || (s === 11);
        if (hit) ev.hat.push({ t, vel: (s % 4 === 0 ? 0.5 : 0.32) });
        // lead : motif clairsemé qui suit la gamme
        if (s % 4 === 0 || s === 6 || s === 14) {
          const deg = sc[(li * 2 + b) % sc.length];
          const oct = (s === 6 || s === 14) ? 12 : 0;
          ev.lead.push({ t, dur: stepDur * 1.6, note: r + 12 + deg + oct });
          li++;
        }
      }
    }
    return ev;
  }

  // ---------- impulsion de reverb (pour l'APRÈS) ----------
  function makeImpulse(c, seconds, decay) {
    const rate = c.sampleRate, len = rate * seconds;
    const imp = c.createBuffer(2, len, rate);
    for (let ch = 0; ch < 2; ch++) {
      const d = imp.getChannelData(ch);
      for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
    }
    return imp;
  }

  // ---------- rendu hors-ligne d'un buffer (avant|après) ----------
  async function renderBuffer(score, mode) {
    const { duration, cfg } = score;
    const sr = 44100;
    const oc = new OfflineAudioContext(2, Math.ceil(sr * (duration + 1.2)), sr);

    // ---- chaîne de sortie (traitement avant/après) ----
    const master = oc.createGain();
    let chainIn = oc.createGain();   // entrée des instruments

    if (mode === 'avant') {
      // IA : mono, band-limité, plat, fizz numérique
      const mono = oc.createGain();
      const hp = oc.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 170; hp.Q.value = 0.5;
      const lp = oc.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 2900; lp.Q.value = 0.4;
      const dip = oc.createBiquadFilter(); dip.type = 'peaking'; dip.frequency.value = 500; dip.gain.value = -3; dip.Q.value = 1;
      // léger waveshaper = grain numérique
      const ws = oc.createWaveShaper();
      const curve = new Float32Array(1024);
      for (let i = 0; i < 1024; i++) { const x = i / 512 - 1; curve[i] = Math.tanh(x * 1.6) * 0.9; }
      ws.curve = curve; ws.oversample = 'none';
      const comp = oc.createDynamicsCompressor();
      comp.threshold.value = -28; comp.ratio.value = 8; comp.attack.value = 0.003; comp.release.value = 0.12;
      chainIn.connect(hp); hp.connect(dip); dip.connect(lp); lp.connect(ws); ws.connect(comp);
      comp.connect(mono); mono.connect(master);
      master.gain.value = 0.62;
    } else {
      // STUDIO : chaud, large, aéré, reverb, punch
      const low = oc.createBiquadFilter(); low.type = 'lowshelf'; low.frequency.value = 130; low.gain.value = 3.5;
      const air = oc.createBiquadFilter(); air.type = 'highshelf'; air.frequency.value = 8500; air.gain.value = 3.5;
      const pres = oc.createBiquadFilter(); pres.type = 'peaking'; pres.frequency.value = 2600; pres.gain.value = 2; pres.Q.value = 0.8;
      const comp = oc.createDynamicsCompressor();
      comp.threshold.value = -18; comp.ratio.value = 2.4; comp.attack.value = 0.012; comp.release.value = 0.18; comp.knee.value = 24;
      // reverb send
      const conv = oc.createConvolver(); conv.buffer = makeImpulse(oc, 1.8, 3.2);
      const wet = oc.createGain(); wet.gain.value = 0.14;
      const dry = oc.createGain(); dry.gain.value = 1.0;
      chainIn.connect(low); low.connect(air); air.connect(pres); pres.connect(comp);
      comp.connect(dry); dry.connect(master);
      comp.connect(conv); conv.connect(wet); wet.connect(master);
      master.gain.value = 0.6;
    }
    master.connect(oc.destination);

    const warm = mode === 'apres';

    // ---- helpers de synthèse ----
    function env(g, t, a, d, s, r, peak, sus) {
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(Math.max(peak, 0.0002), t + a);
      g.gain.exponentialRampToValueAtTime(Math.max(sus, 0.0002), t + a + d);
      g.gain.setValueAtTime(Math.max(sus, 0.0002), t + Math.max(s, a + d));
      g.gain.exponentialRampToValueAtTime(0.0001, t + s + r);
    }
    function pan(node, p) {
      if (oc.createStereoPanner) { const sp = oc.createStereoPanner(); sp.pan.value = warm ? p : 0; node.connect(sp); return sp; }
      return node;
    }

    // pad
    score.pad.forEach((p) => {
      p.notes.forEach((n, idx) => {
        const o = oc.createOscillator(); o.type = warm ? 'sawtooth' : 'square';
        o.frequency.value = mtof(n); o.detune.value = (idx - 1.5) * (warm ? 7 : 2);
        const f = oc.createBiquadFilter(); f.type = 'lowpass';
        f.frequency.value = warm ? 1700 : 1300; f.Q.value = warm ? 0.8 : 0.4;
        const g = oc.createGain();
        const peak = cfg.padGain * (idx === 0 ? 1 : 0.7);
        env(g, p.t, 0.25, 0.4, p.dur, 0.5, peak, peak * 0.7);
        o.connect(f); f.connect(g);
        const out = pan(g, (idx - 1.5) * 0.25);
        out.connect(chainIn);
        o.start(p.t); o.stop(p.t + p.dur + 0.6);
      });
    });

    // bass
    score.bass.forEach((b) => {
      const o = oc.createOscillator(); o.type = 'triangle'; o.frequency.value = mtof(b.note);
      const o2 = oc.createOscillator(); o2.type = 'sine'; o2.frequency.value = mtof(b.note);
      const g = oc.createGain(); env(g, b.t, 0.01, 0.08, b.dur, 0.12, 0.5, 0.34);
      o.connect(g); o2.connect(g); g.connect(chainIn);
      o.start(b.t); o.stop(b.t + b.dur + 0.2); o2.start(b.t); o2.stop(b.t + b.dur + 0.2);
    });

    // lead (pluck)
    score.lead.forEach((l) => {
      const o = oc.createOscillator(); o.type = warm ? 'triangle' : 'sawtooth';
      o.frequency.value = mtof(l.note);
      const f = oc.createBiquadFilter(); f.type = 'lowpass';
      f.frequency.setValueAtTime(warm ? 4200 : 2400, l.t);
      f.frequency.exponentialRampToValueAtTime(warm ? 900 : 700, l.t + l.dur);
      const g = oc.createGain(); env(g, l.t, 0.005, 0.06, l.dur * 0.6, l.dur * 0.5, cfg.leadGain, cfg.leadGain * 0.3);
      o.connect(f); f.connect(g);
      pan(g, (Math.random() - 0.5) * 0.5).connect(chainIn);
      o.start(l.t); o.stop(l.t + l.dur + 0.2);
    });

    // kick
    score.kick.forEach((k) => {
      const o = oc.createOscillator(); o.type = 'sine';
      o.frequency.setValueAtTime(140, k.t); o.frequency.exponentialRampToValueAtTime(45, k.t + 0.12);
      const g = oc.createGain(); g.gain.setValueAtTime(0.9, k.t); g.gain.exponentialRampToValueAtTime(0.0001, k.t + 0.18);
      o.connect(g); g.connect(chainIn); o.start(k.t); o.stop(k.t + 0.2);
    });

    // snare
    score.snare.forEach((s) => {
      const len = 0.2, buf = oc.createBuffer(1, oc.sampleRate * len, oc.sampleRate);
      const d = buf.getChannelData(0); for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
      const n = oc.createBufferSource(); n.buffer = buf;
      const bp = oc.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = 1900; bp.Q.value = 0.7;
      const g = oc.createGain(); g.gain.setValueAtTime(0.5, s.t); g.gain.exponentialRampToValueAtTime(0.0001, s.t + 0.18);
      n.connect(bp); bp.connect(g); g.connect(chainIn); n.start(s.t); n.stop(s.t + len);
    });

    // hats
    score.hat.forEach((h) => {
      const len = 0.05, buf = oc.createBuffer(1, oc.sampleRate * len, oc.sampleRate);
      const d = buf.getChannelData(0); for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
      const n = oc.createBufferSource(); n.buffer = buf;
      const hp = oc.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 7800;
      const g = oc.createGain(); g.gain.setValueAtTime(h.vel, h.t); g.gain.exponentialRampToValueAtTime(0.0001, h.t + 0.045);
      n.connect(hp); hp.connect(g); g.connect(chainIn); n.start(h.t); n.stop(h.t + len);
    });

    const rendered = await oc.startRendering();
    return rendered;
  }

(function () {
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const lerp = (a, b, t) => a + (b - a) * t;
  const smooth = (t) => t * t * (3 - 2 * t);
  // couleurs (rgb)
  const COLD = [150, 162, 196];      // bleu-gris désaturé
  const VIOLET = [123, 92, 246];
  const AMBER = [245, 166, 35];
  const mix = (a, b, t) => [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
  const rgba = (c, a) => `rgba(${c[0] | 0},${c[1] | 0},${c[2] | 0},${a})`;

  class HMViz {
    constructor(canvas, opts) {
      this.c = canvas;
      this.ctx = canvas.getContext('2d');
      this.opts = Object.assign({ layout: 'field', count: 90, getMode: () => 'apres',
        isActive: () => false, isPlaying: () => false, seam: 0.5 }, opts);
      this.warmth = this.opts.getMode() === 'apres' ? 1 : 0;
      this.seam = this.opts.seam;
      this.seamTarget = this.opts.seam;
      this.parts = [];
      this.t0 = performance.now();
      this.dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.resize();
      this.build();
      this.glitch = 0;
      this._raf = 0;
      this._loop = this._loop.bind(this);
      this._ro = new ResizeObserver(() => this.resize());
      this._ro.observe(canvas);
      this.start();
    }
    resize() {
      const r = this.c.getBoundingClientRect();
      this.w = Math.max(1, r.width); this.h = Math.max(1, r.height);
      this.c.width = this.w * this.dpr; this.c.height = this.h * this.dpr;
      this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    }
    build() {
      const n = this.opts.count; this.parts = [];
      for (let i = 0; i < n; i++) {
        this.parts.push({
          i, bx: Math.random(), by: Math.random(),
          ph: Math.random() * Math.PI * 2, sp: 0.4 + Math.random() * 0.9,
          bin: (i * 7) % 220 + 6, e: 0, smoothE: 0,
          ang: (i / n) * Math.PI * 2, rJ: 0.6 + Math.random() * 0.8,
        });
      }
    }
    setSeam(f) { this.seamTarget = clamp(f, 0.04, 0.96); }
    start() { if (!this._raf) this._raf = requestAnimationFrame(this._loop); }
    stop() { cancelAnimationFrame(this._raf); this._raf = 0; this._ro.disconnect(); }

    _energyFor(p, active, playing, time) {
      if (this._sig) {
        const f = this.opts.engine.freq;
        return (f[p.bin % f.length] / 255);
      }
      // repos / audio bloqué : respiration synthétique (jamais figé)
      const base = (active && playing) ? 0.30 : 0.16;
      return base + 0.14 * (0.5 + 0.5 * Math.sin(time * 0.0013 * p.sp + p.ph));
    }

    _loop(now) {
      try {
      const time = now - this.t0;
      const A = this.opts.engine;
      const active = this.opts.isActive();
      const playing = this.opts.isPlaying();
      if (active && A) A.poll();
      // détecte un vrai signal audio (sinon on retombe sur la respiration idle)
      this._sig = false;
      if (active && playing && A && A.freq) {
        let s = 0; for (let i = 4; i < 60; i++) s += A.freq[i];
        this._sig = s > 60;
      }
      const targetWarm = this.opts.getMode() === 'apres' ? 1 : 0;
      this.warmth += (targetWarm - this.warmth) * 0.13;
      this.seam += (this.seamTarget - this.seam) * 0.12;
      // glitch impulse côté froid
      const cold = 1 - this.warmth;
      if (this.opts.layout !== 'split') this.glitch = cold * (active && playing ? 1 : 0.5);

      const ctx = this.ctx; ctx.clearRect(0, 0, this.w, this.h);
      if (this.opts.layout === 'wave') {
        ctx.globalCompositeOperation = 'source-over';
        this._drawWave(time, active, playing);
      } else {
        ctx.globalCompositeOperation = 'lighter';
        if (this.opts.layout === 'orb') this._drawOrb(time, active, playing);
        else if (this.opts.layout === 'split') this._drawSplit(time, active, playing);
        else this._drawField(time, active, playing);
        ctx.globalCompositeOperation = 'source-over';
      }
      } catch (err) { if (window.console) console.warn('HMViz', err); }
      this._raf = requestAnimationFrame(this._loop);
    }

    _partColor(localWarm, e) {
      // froid -> (violet -> ambre selon énergie)
      const warmCol = mix(VIOLET, AMBER, clamp(e * 0.9, 0, 1));
      return mix(COLD, warmCol, localWarm);
    }

    _drawField(time, active, playing) {
      const ctx = this.ctx, W = this.w, H = this.h;
      const pad = 14;
      for (const p of this.parts) {
        const e = this._energyFor(p, active, playing, time);
        p.smoothE += (e - p.smoothE) * 0.25;
        const lw = this.warmth;
        // flux : chaud = ondulation organique ; froid = grille jitter
        const x0 = pad + p.bx * (W - pad * 2);
        const flowY = Math.sin((p.bx * 6) + time * 0.0016 * (0.5 + lw)) * (8 + 20 * lw);
        const jitter = (1 - lw) * (Math.sin(time * 0.02 + p.i) * 2 + (Math.random() - 0.5) * 3 * this.glitch);
        const y0 = pad + p.by * (H - pad * 2);
        const amp = (H * 0.5 - pad) * (0.12 + p.smoothE * 0.8);
        const dir = (p.by < 0.5 ? -1 : 1);
        const x = x0 + (Math.random() - 0.5) * 6 * this.glitch;
        const y = lerp(y0, H / 2 + dir * amp, 0.55) + flowY * lw + jitter;
        const col = this._partColor(lw, p.smoothE);
        const r = (0.9 + p.smoothE * 3.4) * (0.7 + lw * 0.6);
        const a = (0.18 + p.smoothE * 0.7) * (0.55 + lw * 0.45);
        // chromatic split (froid)
        if (this.glitch > 0.05 && Math.random() < 0.25) {
          ctx.fillStyle = rgba([90, 120, 230], a * 0.5 * this.glitch);
          ctx.beginPath(); ctx.arc(x - 3 * this.glitch, y, r, 0, 7); ctx.fill();
          ctx.fillStyle = rgba([230, 90, 140], a * 0.5 * this.glitch);
          ctx.beginPath(); ctx.arc(x + 3 * this.glitch, y, r, 0, 7); ctx.fill();
        }
        ctx.fillStyle = rgba(col, a);
        if (lw > 0.4) { ctx.shadowColor = rgba(col, 0.8); ctx.shadowBlur = 8 * lw; } else ctx.shadowBlur = 0;
        ctx.beginPath(); ctx.arc(x, y, r, 0, 7); ctx.fill();
      }
      ctx.shadowBlur = 0;
    }

    _drawSplit(time, active, playing) {
      const ctx = this.ctx, W = this.w, H = this.h;
      const seamX = this.seam * W;
      for (const p of this.parts) {
        const e = this._energyFor(p, active, playing, time);
        p.smoothE += (e - p.smoothE) * 0.25;
        const x0 = p.bx * W;
        // warmth locale : côté droit chaud, gauche froid, transition douce au niveau du seam
        const d = (x0 - seamX) / (W * 0.14);
        const lw = clamp(smooth(clamp(d * 0.5 + 0.5, 0, 1)), 0, 1);
        const glitch = (1 - lw);
        const flowY = Math.sin((p.bx * 7) + time * 0.0016 * (0.5 + lw)) * (6 + 18 * lw);
        const jitter = glitch * (Math.sin(time * 0.025 + p.i) * 2.4);
        const amp = (H * 0.5 - 10) * (0.12 + p.smoothE * 0.8);
        const dir = (p.by < 0.5 ? -1 : 1);
        const x = x0 + (Math.random() - 0.5) * 5 * glitch * (active && playing ? 1 : 0.4);
        const y = H / 2 + dir * amp * (0.6 + 0.4 * lw) + flowY * lw + jitter;
        const col = this._partColor(lw, p.smoothE);
        const r = (0.9 + p.smoothE * 3.2) * (0.7 + lw * 0.55);
        const a = (0.18 + p.smoothE * 0.7) * (0.5 + lw * 0.5);
        ctx.fillStyle = rgba(col, a);
        if (lw > 0.45) { ctx.shadowColor = rgba(col, 0.8); ctx.shadowBlur = 9 * lw; } else ctx.shadowBlur = 0;
        ctx.beginPath(); ctx.arc(x, y, r, 0, 7); ctx.fill();
      }
      ctx.shadowBlur = 0;
      // trait de couture
      const g = ctx.createLinearGradient(0, 0, 0, H);
      g.addColorStop(0, 'rgba(123,92,246,0)');
      g.addColorStop(0.5, rgba(mix(COLD, AMBER, 0.5), 0.55));
      g.addColorStop(1, 'rgba(123,92,246,0)');
      ctx.strokeStyle = g; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.moveTo(seamX, 8); ctx.lineTo(seamX, H - 8); ctx.stroke();
    }

    _drawOrb(time, active, playing) {
      const ctx = this.ctx, W = this.w, H = this.h;
      const cx = W / 2, cy = H / 2;
      const baseR = Math.min(W, H) * 0.20;
      const lw = this.warmth;
      // halo central (chaud)
      if (lw > 0.02) {
        const gr = ctx.createRadialGradient(cx, cy, baseR * 0.2, cx, cy, baseR * 2.4);
        gr.addColorStop(0, rgba(mix(VIOLET, AMBER, 0.35), 0.22 * lw));
        gr.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = gr; ctx.beginPath(); ctx.arc(cx, cy, baseR * 2.4, 0, 7); ctx.fill();
      }
      for (const p of this.parts) {
        const e = this._energyFor(p, active, playing, time);
        p.smoothE += (e - p.smoothE) * 0.22;
        const wob = (1 - lw) * (Math.sin(time * 0.02 + p.i * 1.3) * 0.5 + 0.5) * p.rJ;
        const ringJitter = (1 - lw) * (Math.random() - 0.5) * 0.5;
        const rr = baseR * (1 + p.smoothE * 1.4) + baseR * (wob * 0.6 + ringJitter) ;
        const ang = p.ang + time * 0.00018 * (lw ? 1 : 0.3) * (p.i % 2 ? 1 : -1);
        const x = cx + Math.cos(ang) * rr;
        const y = cy + Math.sin(ang) * rr * 0.92;
        const col = this._partColor(lw, p.smoothE);
        const r = (0.9 + p.smoothE * 3.0) * (0.7 + lw * 0.7);
        const a = (0.2 + p.smoothE * 0.7) * (0.5 + lw * 0.5);
        if (this.glitch > 0.05 && Math.random() < 0.2) {
          ctx.fillStyle = rgba([90, 120, 230], a * 0.5);
          ctx.beginPath(); ctx.arc(x - 2.5, y, r, 0, 7); ctx.fill();
          ctx.fillStyle = rgba([230, 90, 140], a * 0.5);
          ctx.beginPath(); ctx.arc(x + 2.5, y, r, 0, 7); ctx.fill();
        }
        ctx.fillStyle = rgba(col, a);
        if (lw > 0.4) { ctx.shadowColor = rgba(col, 0.9); ctx.shadowBlur = 10 * lw; } else ctx.shadowBlur = 0;
        ctx.beginPath(); ctx.arc(x, y, r, 0, 7); ctx.fill();
      }
      ctx.shadowBlur = 0;
    }

    _drawWave(time, active, playing) {
      const ctx = this.ctx, W = this.w, H = this.h;
      if (W < 2 || H < 2) return;
      const nowS = time / 1000;
      if (this._pt == null) this._pt = nowS;
      let dt = nowS - this._pt; this._pt = nowS;
      if (dt > 0.05) dt = 0.05; if (dt < 0) dt = 0;
      if (this._wt == null) this._wt = 0;
      if (this._en == null) this._en = 0.82;
      const pl = !!(active && playing);
      this._en += ((pl ? 1 : 0.82) - this._en) * Math.min(1, dt * 3.2);
      if (pl) this._wt += dt;
      const t = this._wt, energy = this._en, m = clamp(this.warmth, 0, 1);
      const AV = { fillTop:[148,154,228], fillBot:[116,124,188], lineL:[92,100,178], lineR:[132,140,210], glow:[106,107,255], reflect:[138,146,206] };
      const AP = { fillTop:[106,107,255], fillBot:[245,166,35], lineL:[106,107,255], lineR:[245,166,35], glow:[245,166,35], reflect:[206,150,118] };
      const cur = {
        fillTop: mix(AV.fillTop, AP.fillTop, m), fillBot: mix(AV.fillBot, AP.fillBot, m),
        lineL: mix(AV.lineL, AP.lineL, m), lineR: mix(AV.lineR, AP.lineR, m),
        glow: mix(AV.glow, AP.glow, m), reflect: mix(AV.reflect, AP.reflect, m)
      };
      const cy = H * 0.54, maxAmp = H * 0.46;
      const breath = 1 + lerp(0.03, 0.14, m) * Math.sin(t * 0.85 + 0.5);
      const step = Math.max(2, Math.round(W / 380));
      const profA = (u) => 0.32 + 0.25 * (0.50*Math.sin(u*6.2+t*1.05) + 0.28*Math.sin(u*10.6-t*1.50+1.0) + 0.16*Math.sin(u*3.0+t*0.60));
      const profB = (u) => 0.42 + 0.42 * (0.42*Math.sin(u*7.4+t*1.25) + 0.26*Math.sin(u*12.8-t*1.85+0.6) + 0.17*Math.sin(u*18.6+t*2.30+1.7) + 0.30*Math.sin(u*3.6-t*0.85) + 0.10*Math.sin(u*25.0+t*3.00));
      const pts = [];
      for (let x = 0; x <= W; x += step) {
        const u = x / W;
        const env = Math.pow(Math.sin(Math.PI * u), 0.7);
        const a = lerp(profA(u), profB(u), m);
        let amp = env * a * energy * breath * maxAmp;
        if (amp < 0) amp = 0;
        pts.push([x, cy - amp]);
      }
      if (pts.length < 2) return;
      if (m > 0.02) {
        const gg = ctx.createRadialGradient(W*0.5, cy-maxAmp*0.35, 8, W*0.5, cy-maxAmp*0.35, Math.max(W,H)*0.55);
        gg.addColorStop(0, rgba(AP.glow, 0.11*m)); gg.addColorStop(1, rgba(AP.glow, 0));
        ctx.fillStyle = gg; ctx.fillRect(0,0,W,H);
      }
      ctx.save(); ctx.beginPath(); ctx.moveTo(0, cy);
      for (let i=0;i<pts.length;i++){ ctx.lineTo(pts[i][0], cy + (cy-pts[i][1])*0.5); }
      ctx.lineTo(W, cy); ctx.closePath();
      const rg = ctx.createLinearGradient(0, cy, 0, cy+maxAmp*0.5);
      rg.addColorStop(0, rgba(cur.reflect, 0.30)); rg.addColorStop(1, rgba(cur.reflect, 0));
      ctx.fillStyle = rg; ctx.fill(); ctx.restore();
      ctx.beginPath(); ctx.moveTo(0, cy);
      for (let i=0;i<pts.length;i++){ ctx.lineTo(pts[i][0], pts[i][1]); }
      ctx.lineTo(W, cy); ctx.closePath();
      const fg = ctx.createLinearGradient(0, cy-maxAmp, 0, cy);
      fg.addColorStop(0.00, rgba(cur.fillTop, 0.94));
      fg.addColorStop(0.55, rgba(mix(cur.fillTop, cur.fillBot, 0.5), 0.55));
      fg.addColorStop(1.00, rgba(cur.fillBot, 0.10));
      ctx.fillStyle = fg; ctx.fill();
      ctx.beginPath(); ctx.moveTo(pts[0][0], pts[0][1]);
      for (let i=1;i<pts.length;i++){ ctx.lineTo(pts[i][0], pts[i][1]); }
      const lg = ctx.createLinearGradient(0,0,W,0);
      lg.addColorStop(0, rgba(cur.lineL,1)); lg.addColorStop(1, rgba(cur.lineR,1));
      ctx.lineJoin='round'; ctx.lineCap='round'; ctx.strokeStyle=lg; ctx.lineWidth=2.4;
      ctx.shadowColor = rgba(cur.glow, 0.55); ctx.shadowBlur = 14 + 9*m; ctx.stroke(); ctx.shadowBlur = 0;
      ctx.beginPath(); ctx.moveTo(pts[0][0], pts[0][1]);
      for (let i=1;i<pts.length;i++){ ctx.lineTo(pts[i][0], pts[i][1]); }
      ctx.strokeStyle='rgba(255,255,255,0.16)'; ctx.lineWidth=1; ctx.stroke();
    }
  }
  window.HMViz = HMViz;
})();

  // ===========================================================
  // MOTEUR (une instance par lecteur)
  // ===========================================================
  function HMEngine() {
    var self = {};
    var ctx = getCtx();
    var analyser = ctx.createAnalyser();
    analyser.fftSize = 1024; analyser.smoothingTimeConstant = 0.82;
    analyser.connect(ctx.destination);
    self.analyser = analyser;
    self.freq = new Uint8Array(analyser.frequencyBinCount);
    self.wave = new Uint8Array(analyser.fftSize);
    self.poll = function () { analyser.getByteFrequencyData(self.freq); analyser.getByteTimeDomainData(self.wave); };

    self.state = { ready: false, playing: false, mode: 'avant', genre: 'pop', example: 0, position: 0, duration: 0, loading: false };
    self.realSources = null;
    self.avantGain = 1;

    var listeners = [];
    self.subscribe = function (fn) { listeners.push(fn); return function () { var i = listeners.indexOf(fn); if (i >= 0) listeners.splice(i, 1); }; };
    function emit() { for (var i = 0; i < listeners.length; i++) listeners[i](self.state); }

    var cache = {};
    function curKey() { return self.state.genre + '#' + (self.state.example || 0); }
    function getBuffers(genre, idx) {
      idx = idx || 0;
      var key = genre + '#' + idx;
      if (cache[key]) return Promise.resolve(cache[key]);
      var rs = self.realSources && self.realSources[genre];
      var pair = Array.isArray(rs) ? rs[idx] : (idx === 0 ? rs : null);
      if (pair && pair.avant && pair.apres) {
        var load = function (url) { return fetch(url).then(function (r) { return r.arrayBuffer(); }).then(function (ab) { return ctx.decodeAudioData(ab); }); };
        return Promise.all([load(pair.avant), load(pair.apres)]).then(function (res) {
          cache[key] = { avant: res[0], apres: res[1], duration: Math.min(res[0].duration, res[1].duration) };
          return cache[key];
        });
      }
      var score = buildScore(genre, idx);
      return Promise.all([renderBuffer(score, 'avant'), renderBuffer(score, 'apres')]).then(function (res) {
        cache[key] = { avant: res[0], apres: res[1], duration: score.duration };
        return cache[key];
      });
    }

    var srcAvant = null, srcApres = null, gAvant = null, gApres = null, startedAt = 0, startOffset = 0, rafId = 0, progTimer = 0, wallStart = 0;
    function stopSources() { [srcAvant, srcApres].forEach(function (s) { if (s) { try { s.onended = null; s.stop(); } catch (e) {} } }); srcAvant = srcApres = null; }
    function advance() {
      if (!self.state.playing) return false;
      var pos = startOffset + (performance.now() - wallStart) / 1000;
      if (pos >= self.state.duration) { startFrom(0); return false; }
      self.state.position = pos; emit();
      return true;
    }
    function tick() { if (advance()) rafId = requestAnimationFrame(tick); }
    function startFrom(offset) {
      stopSources();
      var bufs = cache[curKey()]; if (!bufs) return;
      gAvant = ctx.createGain(); gApres = ctx.createGain();
      gAvant.connect(analyser); gApres.connect(analyser);
      var isA = self.state.mode === 'avant';
      gAvant.gain.value = isA ? self.avantGain : 0.0001; gApres.gain.value = isA ? 0.0001 : 1;
      srcAvant = ctx.createBufferSource(); srcAvant.buffer = bufs.avant;
      srcApres = ctx.createBufferSource(); srcApres.buffer = bufs.apres;
      srcAvant.connect(gAvant); srcApres.connect(gApres);
      var when = ctx.currentTime + 0.02; var off = offset % self.state.duration;
      srcAvant.start(when, off); srcApres.start(when, off);
      startedAt = when; startOffset = off; wallStart = performance.now(); self.state.position = off;
      cancelAnimationFrame(rafId); rafId = requestAnimationFrame(tick);
      clearInterval(progTimer); progTimer = setInterval(advance, 250);
    }

    self.play = function (genre, ex) {
      BUS.claim(self);
      return Promise.resolve(ctx.state === 'suspended' ? ctx.resume() : null).then(function () {
        if (genre && genre !== self.state.genre) { self.state.genre = genre; self.state.example = 0; }
        if (typeof ex === 'number') self.state.example = ex;
        self.state.loading = !cache[curKey()]; emit();
        return getBuffers(self.state.genre, self.state.example);
      }).then(function (bufs) {
        self.state.duration = bufs.duration; self.state.loading = false; self.state.ready = true; self.state.playing = true;
        startFrom(self.state.position || 0); emit();
      });
    };
    self.pause = function () {
      if (!self.state.playing) return;
      self.state.position = startOffset + (performance.now() - wallStart) / 1000;
      self.state.playing = false; stopSources(); cancelAnimationFrame(rafId); clearInterval(progTimer); BUS.release(self); emit();
    };
    self.setMode = function (mode) {
      if (mode === self.state.mode) return;
      self.state.mode = mode;
      if (gAvant && gApres) {
        var t = ctx.currentTime, isA = mode === 'avant', ramp = 0.075;
        gAvant.gain.cancelScheduledValues(t); gApres.gain.cancelScheduledValues(t);
        gAvant.gain.setValueAtTime(Math.max(gAvant.gain.value, 0.0001), t);
        gApres.gain.setValueAtTime(Math.max(gApres.gain.value, 0.0001), t);
        gAvant.gain.linearRampToValueAtTime(isA ? self.avantGain : 0, t + ramp);
        gApres.gain.linearRampToValueAtTime(isA ? 0 : 1, t + ramp);
      }
      emit();
    };
    self.setGenre = function (genre) {
      self.state.genre = genre; self.state.example = 0; self.state.loading = true; emit();
      return getBuffers(genre, 0).then(function (b) { self.state.duration = b.duration; self.state.loading = false; if (self.state.playing) startFrom(0); emit(); });
    };
    self.setExample = function (idx) {
      self.state.example = idx; self.state.loading = true; emit();
      return getBuffers(self.state.genre, idx).then(function (b) { self.state.duration = b.duration; self.state.loading = false; if (self.state.playing) startFrom(0); emit(); });
    };
    self.seek = function (frac) {
      var dur = self.state.duration || 1; var off = Math.max(0, Math.min(0.999, frac)) * dur;
      if (self.state.playing) startFrom(off); else { self.state.position = off; startOffset = off; emit(); }
    };
    self.fmt = function (s) { s = Math.max(0, s | 0); var m = (s / 60) | 0, ss = s % 60; return m + ':' + (ss < 10 ? '0' : '') + ss; };
    return self;
  }

  // ===========================================================
  // MONTAGE D'UN LECTEUR dans un conteneur
  // ===========================================================
  function mount(root, CFG) {
    CFG = CFG || {};
    if (!root || root.__hmMounted) return; root.__hmMounted = true;
    var engine = HMEngine();
    if (CFG.realSources) engine.realSources = CFG.realSources;
    engine.avantGain = (typeof CFG.avantGainDb === 'number' && CFG.avantGainDb) ? Math.pow(10, CFG.avantGainDb / 20) : 1;

    var GENRES = CFG.genres || [
      { id: 'drill', label: 'Drill' }, { id: 'pop', label: 'Pop' },
      { id: 'afro', label: 'Afro' }, { id: 'rnb', label: 'RNB' }
    ];
    var CTA_URL = CFG.ctaUrl || '#';
    var CTA_LABEL = CFG.ctaLabel || 'Faire réarranger ma chanson IA';
    var PROOF = CFG.proof || '';
    var TITLES = { avant: CFG.titleAvant || 'Plate. Froide.\nSans âme.', apres: CFG.titleApres || 'Vivante. Chaude.\nÀ toi.' };
    var OVERLINE = (CFG.overline != null && CFG.overline !== '') ? CFG.overline : 'Avant / Après';
    var SUB_AVANT = (CFG.subAvant != null && CFG.subAvant !== '') ? CFG.subAvant : 'Généré par IA';
    var SUB_APRES = (CFG.subApres != null && CFG.subApres !== '') ? CFG.subApres : 'Hedayat';

    var MARK = '<svg class="hm-mark" viewBox="0 0 200 132" width="32" height="21" aria-hidden="true"><g fill="none" stroke="currentColor" stroke-width="9"><rect x="40" y="6" width="28" height="95" rx="9"/><rect x="101.5" y="6" width="27" height="95" rx="9"/><rect x="130" y="6" width="27" height="95" rx="9"/></g><rect x="71" y="26" width="26" height="100" rx="9" fill="currentColor"/></svg>';
    var IC_PLAY = '<svg viewBox="0 0 24 24" width="100%" height="100%"><path d="M8 5.5v13l11-6.5z" fill="currentColor"/></svg>';
    var IC_PAUSE = '<svg viewBox="0 0 24 24" width="100%" height="100%"><rect x="7" y="5.5" width="3.4" height="13" rx="1.2" fill="currentColor"/><rect x="13.6" y="5.5" width="3.4" height="13" rx="1.2" fill="currentColor"/></svg>';
    var IC_ARROW = '<svg viewBox="0 0 24 24" width="1em" height="1em" style="margin-left:8px;vertical-align:-2px"><path d="M4 12h15M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    engine.state.genre = GENRES[0].id;
    var genrePills = GENRES.map(function (g) { return '<button class="hm-genre" type="button" aria-pressed="false" data-genre="' + g.id + '">' + g.label + '</button>'; }).join('');

    root.innerHTML =
      '<div class="hm-player halo warm" data-player>' +
        '<div class="hm-halo-bg"></div>' +
        '<div class="hm-halo-top">' +
          '<div class="hm-overline light">' + MARK + ' ' + OVERLINE + '</div>' +
          '<h3 class="hm-title big" data-title></h3>' +
        '</div>' +
        '<div class="hm-ab hm-ab-md hm-ab-wave" role="group" aria-label="Avant ou Après">' +
          '<span class="hm-ab-glow right" data-glow></span>' +
          '<button class="hm-ab-btn" data-mode="avant" aria-pressed="false" aria-label="Avant, ' + SUB_AVANT + '"><strong>Avant</strong><em>' + SUB_AVANT + '</em></button>' +
          '<button class="hm-ab-btn warm active" data-mode="apres" aria-pressed="true" aria-label="Après, ' + SUB_APRES + '"><strong>Après</strong><em>' + SUB_APRES + '</em></button>' +
        '</div>' +
        '<div class="hm-orb hm-orb-wave" data-orb>' +
          '<canvas class="hm-canvas" data-viz></canvas>' +
        '</div>' +
        '<div class="hm-wave-ctrl">' +
          '<button class="hm-orb-play hm-wave-play is-warm" data-play aria-label="Lecture"></button>' +
          '<span class="hm-time-c" data-cur>0:00</span>' +
          '<div class="hm-prog is-warm" data-prog role="slider" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0" tabindex="0">' +
            '<div class="hm-prog-fill" data-fill></div><div class="hm-prog-knob" data-knob></div>' +
          '</div>' +
          '<span class="hm-time-c" data-dur>0:00</span>' +
        '</div>' +
        '<div class="hm-halo-foot">' +
          '<div class="hm-genres" role="group" aria-label="Style musical">' + genrePills + '</div>' +
          '<div class="hm-examples" data-examples></div>' +
          (CTA_LABEL ? '<a class="hm-cta" data-cta>' + CTA_LABEL + IC_ARROW + '</a>' : '') +
        '</div>' +
        (PROOF ? '<div class="hm-proof light"><span data-proof></span></div>' : '') +
      '</div>';

    var $ = function (s) { return root.querySelector(s); };
    var playerEl = $('[data-player]'), titleEl = $('[data-title]'), playBtn = $('[data-play]');
    var glow = $('[data-glow]'), prog = $('[data-prog]'), fill = $('[data-fill]'), knob = $('[data-knob]');
    var curEl = $('[data-cur]'), durEl = $('[data-dur]'), cta = $('[data-cta]');
    if (PROOF) $('[data-proof]').innerHTML = PROOF;
    if (cta) cta.setAttribute('href', CTA_URL);

    var mode = 'avant', genre = GENRES[0].id, example = 0;
    var _btnSig = '';

    var canvas = $('[data-viz]');
    new window.HMViz(canvas, {
      layout: 'wave', count: window.innerWidth < 560 ? 84 : 130,
      getMode: function () { return mode; },
      isActive: function () { return engine.state.playing; },
      isPlaying: function () { return engine.state.playing; },
      engine: engine
    });

    function exsFor(id) {
      var gd = GENRES.filter(function (g) { return g.id === id; })[0];
      if (gd && gd.examples && gd.examples.length) return gd.examples;
      var rs = engine.realSources && engine.realSources[id];
      if (rs && rs.length) return rs.map(function (_, i) { return { label: 'Ex. ' + (i + 1) }; });
      return [{ label: 'Ex. 1' }];
    }
    function buildExamples() {
      var exs = exsFor(genre); var box = $('[data-examples]');
      box.innerHTML = exs.map(function (e, i) { return '<button class="hm-ex" data-ex="' + i + '">' + (e.label || ('Ex. ' + (i + 1))) + '</button>'; }).join('');
      box.style.display = exs.length > 1 ? 'flex' : 'none';
      box.querySelectorAll('[data-ex]').forEach(function (b) { b.addEventListener('click', function () { setExample(+b.getAttribute('data-ex')); }); });
    }

    function toggle() { _iosUnlock(); if (engine.state.playing) engine.pause(); else { engine.setMode(mode); engine.play(genre, example); } }
    function setMode(m) { mode = m; engine.setMode(m); render(); }
    function setGenre(g) { genre = g; example = 0; buildExamples(); if (engine.state.playing) engine.setGenre(g); render(); }
    function setExample(i) { example = i; if (engine.state.playing) engine.setExample(i); render(); }
    function seek(frac) { _iosUnlock(); if (engine.state.playing) engine.seek(frac); else { engine.setMode(mode); engine.play(genre, example).then(function () { engine.seek(frac); }); } }

    playBtn.addEventListener('click', function (e) { e.stopPropagation(); toggle(); });
    $('[data-orb]').addEventListener('click', toggle);
    root.querySelectorAll('[data-mode]').forEach(function (b) { b.addEventListener('click', function () { setMode(b.getAttribute('data-mode')); }); });
    root.querySelectorAll('[data-genre]').forEach(function (b) { b.addEventListener('click', function () { setGenre(b.getAttribute('data-genre')); }); });
    function handle(e) { var r = prog.getBoundingClientRect(); seek(Math.max(0, Math.min(1, (e.clientX - r.left) / r.width))); }
    prog.addEventListener('pointerdown', function (e) {
      prog.setPointerCapture(e.pointerId); handle(e);
      var mv = function (ev) { handle(ev); };
      var up = function () { window.removeEventListener('pointermove', mv); window.removeEventListener('pointerup', up); };
      window.addEventListener('pointermove', mv); window.addEventListener('pointerup', up);
    });

    function render() {
      var playing = engine.state.playing, loading = engine.state.loading;
      playerEl.className = 'hm-player halo ' + (mode === 'avant' ? 'cold' : 'warm');
      titleEl.textContent = TITLES[mode];
      playBtn.className = 'hm-orb-play hm-wave-play ' + (mode === 'avant' ? 'is-cold' : 'is-warm');
      var _sig = (loading ? 'L' : (playing ? 'P' : 'p'));
      if (_sig !== _btnSig) {
        _btnSig = _sig;
        playBtn.innerHTML = '<span>' + (loading ? '<span class="hm-spin"></span>' : (playing ? IC_PAUSE : IC_PLAY)) + '</span>';
        playBtn.setAttribute('aria-label', playing ? 'Pause' : 'Lecture');
      }
      glow.className = 'hm-ab-glow ' + (mode === 'avant' ? 'left' : 'right');
      root.querySelectorAll('[data-mode]').forEach(function (b) { var on = b.getAttribute('data-mode') === mode; b.classList.toggle('active', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
      root.querySelectorAll('[data-genre]').forEach(function (b) { var on = b.getAttribute('data-genre') === genre; b.classList.toggle('active', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
      root.querySelectorAll('[data-ex]').forEach(function (b) { b.classList.toggle('active', (+b.getAttribute('data-ex')) === example); });
      prog.className = 'hm-prog ' + (mode === 'avant' ? 'is-cold' : 'is-warm');
      var pos = engine.state.position, dur = engine.state.duration;
      var frac = dur ? Math.min(1, pos / dur) : 0;
      fill.style.width = (frac * 100) + '%'; knob.style.left = (frac * 100) + '%';
      fill.style.height = '6px';
      fill.style.background = (mode === 'avant') ? 'linear-gradient(90deg,#8b93e6,#cdd3fb)' : 'linear-gradient(90deg,#6a6bff,#f5a623)';
      fill.style.boxShadow = '0 0 10px rgba(150,160,235,.5)';
      knob.style.setProperty('display', 'block', 'important');
      knob.style.width = '16px'; knob.style.height = '16px'; knob.style.background = '#fff'; knob.style.borderRadius = '50%'; knob.style.zIndex = '3';
      knob.style.boxShadow = '0 0 0 4px rgba(139,147,235,.45), 0 2px 10px rgba(0,0,0,.55)';
      curEl.textContent = engine.fmt(pos); durEl.textContent = dur ? engine.fmt(dur) : '0:00';
      prog.setAttribute('aria-valuenow', Math.round(frac * 100));
    }
    engine.subscribe(render);
    buildExamples();
    render();
  }

  function boot() {
    var els = document.querySelectorAll('[data-hm-avantapres]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i]; if (el.__hmMounted) continue;
      var cfg = {}; var s = el.querySelector('script[type="application/json"]');
      if (s) { try { cfg = JSON.parse(s.textContent); } catch (e) {} }
      mount(el, cfg);
    }
  }
  window.HMAvantApres = { mount: mount, boot: boot };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
